import axios, { AxiosProgressEvent } from 'axios'
import { FILE_HOST } from 'utils/helpers/apiHelper'
import { FixMeLater } from 'types'
import { captureHandledError } from 'utils/sentry'


let controller = new AbortController()

const shouldCaptureAxiosError = (error: unknown) => {
  if (axios.isCancel(error)) return false

  if (axios.isAxiosError(error) && error.response?.status) {
    return error.response.status >= 500
  }

  return true
}

const captureAxiosError = (error: unknown, request: Record<string, unknown>) => {
  if (!shouldCaptureAxiosError(error)) return

  captureHandledError(error, {
    tags: {
      source: 'axios',
      status: axios.isAxiosError(error) ? error.response?.status : undefined,
    },
    extra: {
      request,
      response: axios.isAxiosError(error)
        ? {
            status: error.response?.status,
            data: error.response?.data,
          }
        : undefined,
    },
  })
}



const axiosApi = {
  setHeader: async (accessToken: string) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  },
  request: async (method: string, path: string, params?: FixMeLater, config?: FixMeLater) => {
    try {
      return await axios({
        method,
        url: `${FILE_HOST}${path}`,
        data: params,
        ...config,
        headers: {
          ...axios.defaults.headers.common,
          ...config?.headers,
        },
      })
    } catch (error) {
      captureAxiosError(error, { method, path })
      throw error
    }
  },
  uploadFile: async (
    path: string,
    files: File[],
    appendName: string,
    formDataParams?: FixMeLater,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
    accessToken?: string,
  ) => {
    controller = new AbortController()
    const formData = new FormData()
    files.forEach((file) => {
      formData.append(appendName, file)
    })
    if (formDataParams) {
      Object.entries(formDataParams).forEach(([key, value]) => {
        formData.append(key, JSON.stringify(value))
      })
    }
    try {
      return await axios({
        method: 'POST',
        url: `${FILE_HOST}/${path}`,
        data: formData,
        onUploadProgress,
        signal: controller.signal,
        timeout: 300000, // 5 dakika timeout (büyük dosyalar için)
        headers: {
          ...axios.defaults.headers.common,
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      })
    } catch (error) {
      captureAxiosError(error, { method: 'POST', path, upload: true })
      throw error
    }
  },
  clearToken: () => {
    axios.defaults.headers.common['Authorization'] = ''
  },
  cancelUploadFile: () => {
    controller.abort()
  },
  downloadFileWithUrl: async (url: string) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      captureHandledError(error, {
        tags: {
          source: 'file_download',
        },
        extra: {
          url,
        },
      })
      console.error('File download failed:', error);
      throw error;
    }
  },
    getPresignedUrl: async (filePath: string, accessToken?: string): Promise<string> => {
    try {
   
      const response = await axios({
        method: 'GET',
        url: `${FILE_HOST}${filePath}`,
        headers: {
          ...axios.defaults.headers.common,
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const { presignedUrl } = response.data;
      return presignedUrl;
    } catch (error) {
      captureAxiosError(error, { method: 'GET', path: filePath, presignedUrl: true })
      console.error('Presigned URL fetch failed:', error);
      throw error;
    }
  },
  get: (path: string) => axiosApi.request('GET', path),
  post: (path: string, params: FixMeLater) => axiosApi.request('POST', path, params),
  put: (path: string, params?: FixMeLater) => axiosApi.request('PUT', path, params),
}

export default axiosApi
